import { useState, useMemo, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { useWorld } from '@/hooks/useWorld'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { createNation } from '@/services/nations'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { GovernmentType, ElectoralSystem, JudicialReviewStrength, MediaFreedom } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function NationCreate() {
  const { worldId } = useParams<{ worldId: string }>()
  const navigate = useNavigate()
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()
  const { world } = useWorld(worldId)

  // Basic Info
  const [name, setName] = useState('')

  // Government Structure
  const [governmentType, setGovernmentType] = useState<GovernmentType>('parliamentary')
  const [electoralSystem, setElectoralSystem] = useState<ElectoralSystem>('nationwide_pr')
  const [termLengthTicks, setTermLengthTicks] = useState('16')
  const [confidenceVotesEnabled, setConfidenceVotesEnabled] = useState(true)

  // Electoral Rules
  const [thresholdPercent, setThresholdPercent] = useState('5')
  const [maxParties, setMaxParties] = useState('8')

  // Institutions
  const [judicialReviewStrength, setJudicialReviewStrength] =
    useState<JudicialReviewStrength>('weak')
  const [mediaFreedom, setMediaFreedom] = useState<MediaFreedom>('free')

  // Form state
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    items.push({ label: 'Create Nation' })
    return items
  }, [world])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    const termLength = Number(termLengthTicks)
    if (!termLength || termLength < 1) {
      setError('Term length must be at least 1 tick')
      return
    }

    const threshold = Number(thresholdPercent)
    if (isNaN(threshold) || threshold < 0 || threshold > 100) {
      setError('Threshold must be between 0 and 100')
      return
    }

    const parties = Number(maxParties)
    if (!parties || parties < 2) {
      setError('Max parties must be at least 2')
      return
    }

    if (!worldId) {
      setError('World ID is missing')
      return
    }

    setIsSubmitting(true)

    try {
      const nation = await createNation({
        world_id: worldId,
        name: name.trim(),
        government_type: governmentType,
        electoral_system: electoralSystem,
        term_length_ticks: termLength,
        confidence_votes_enabled: confidenceVotesEnabled,
        threshold_percent: threshold,
        max_parties: parties,
        judicial_review_strength: judicialReviewStrength,
        media_freedom: mediaFreedom,
        campaign_restrictions: {},
      })
      navigate(`/nations/${nation.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create nation')
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(`/worlds/${worldId}`)
  }

  // Show loading while checking admin status
  if (isAdminLoading) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </AppLayout>
    )
  }

  // Redirect non-admins
  if (!isAdmin) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Only administrators can create new nations.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={() => navigate(`/worlds/${worldId}`)}>
              Back to World
            </Button>
          </CardFooter>
        </Card>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Card className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle>Create New Nation</CardTitle>
          <CardDescription>
            Configure the constitutional rules and institutions for a new nation.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-8">
            {error && (
              <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                {error}
              </div>
            )}

            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Info</h3>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter nation name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Government Structure Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Government Structure</h3>
              <div className="space-y-2">
                <Label htmlFor="governmentType">Government Type</Label>
                <Select
                  value={governmentType}
                  onValueChange={(value: string) => setGovernmentType(value as GovernmentType)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="governmentType">
                    <SelectValue placeholder="Select government type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parliamentary">Parliamentary</SelectItem>
                    <SelectItem value="semi_presidential">Semi-Presidential</SelectItem>
                    <SelectItem value="presidential">Presidential</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  Determines the relationship between executive and legislature.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="electoralSystem">Electoral System</Label>
                <Select
                  value={electoralSystem}
                  onValueChange={(value: string) => setElectoralSystem(value as ElectoralSystem)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="electoralSystem">
                    <SelectValue placeholder="Select electoral system" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nationwide_pr">Nationwide PR</SelectItem>
                    <SelectItem value="regional_pr">Regional PR</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  How votes are translated into legislative seats.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="termLengthTicks">Term Length (in ticks)</Label>
                <Input
                  id="termLengthTicks"
                  type="number"
                  min="1"
                  placeholder="16"
                  value={termLengthTicks}
                  onChange={(e) => setTermLengthTicks(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
                <p className="text-muted-foreground text-xs">
                  16 ticks = 4-year election cycle. 1 tick = 3 in-game months.
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="confidenceVotesEnabled"
                  checked={confidenceVotesEnabled}
                  onCheckedChange={setConfidenceVotesEnabled}
                  disabled={isSubmitting}
                />
                <div className="space-y-1">
                  <Label htmlFor="confidenceVotesEnabled">Confidence Votes Enabled</Label>
                  <p className="text-muted-foreground text-xs">
                    Allow legislature to remove government via no-confidence vote.
                  </p>
                </div>
              </div>
            </div>

            {/* Electoral Rules Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Electoral Rules</h3>
              <div className="space-y-2">
                <Label htmlFor="thresholdPercent">Threshold Percent</Label>
                <Input
                  id="thresholdPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="5"
                  value={thresholdPercent}
                  onChange={(e) => setThresholdPercent(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
                <p className="text-muted-foreground text-xs">
                  Minimum vote percentage required for parliamentary representation (0-100).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxParties">Max Parties</Label>
                <Input
                  id="maxParties"
                  type="number"
                  min="2"
                  placeholder="8"
                  value={maxParties}
                  onChange={(e) => setMaxParties(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
                <p className="text-muted-foreground text-xs">
                  Maximum number of political parties allowed (minimum 2).
                </p>
              </div>
            </div>

            {/* Institutions Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Institutions</h3>
              <div className="space-y-2">
                <Label htmlFor="judicialReviewStrength">Judicial Review Strength</Label>
                <Select
                  value={judicialReviewStrength}
                  onValueChange={(value: string) =>
                    setJudicialReviewStrength(value as JudicialReviewStrength)
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="judicialReviewStrength">
                    <SelectValue placeholder="Select judicial review strength" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="weak">Weak</SelectItem>
                    <SelectItem value="strong">Strong</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  Court's ability to strike down unconstitutional laws.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mediaFreedom">Media Freedom</Label>
                <Select
                  value={mediaFreedom}
                  onValueChange={(value: string) => setMediaFreedom(value as MediaFreedom)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="mediaFreedom">
                    <SelectValue placeholder="Select media freedom level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="state_influenced">State Influenced</SelectItem>
                    <SelectItem value="polarized">Polarized</SelectItem>
                    <SelectItem value="censored">Censored</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  Affects how information spreads and campaign effectiveness.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex gap-3">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Nation'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AppLayout>
  )
}
